/**
 * Modal showing read-only details for a single endpoint on the infrastructure map.
 */

import type { ReactElement } from 'react'

interface EndpointForDetails {
  id: string
  hostname: string
  ipAddresses?: string[] | null
  deviceType?: string | null
  operatingSystem?: string | null
  osVersion?: string | null
  domain?: string | null
  ouPath?: string | null
  roles?: string[] | null
  importance?: string | null
  notes?: string | null
}

interface EndpointDetailsModalProps {
  endpoint: EndpointForDetails
  onClose: () => void
}

export function EndpointDetailsModal({
  endpoint,
  onClose,
}: EndpointDetailsModalProps): ReactElement {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="mx-4 w-full max-w-md overflow-hidden rounded-xl bg-white shadow-2xl dark:bg-slate-800">
        <Header onClose={onClose} />
        <div className="max-h-[60vh] space-y-3 overflow-y-auto px-5 py-4">
          <PrimaryDetails endpoint={endpoint} />
          <SystemDetails endpoint={endpoint} />
          <OrganisationalDetails endpoint={endpoint} />
          {endpoint.roles && endpoint.roles.length > 0 && <RolesField roles={endpoint.roles} />}
          {endpoint.importance && <ImportanceField importance={endpoint.importance} />}
          {endpoint.notes && <NotesField notes={endpoint.notes} />}
          <div className="border-t border-slate-200 pt-2 dark:border-slate-700">
            <Field label="ID" value={endpoint.id} idStyle />
          </div>
        </div>
      </div>
    </div>
  )
}

function PrimaryDetails({ endpoint }: { endpoint: EndpointForDetails }): ReactElement {
  return (
    <>
      <Field label="Hostname" value={endpoint.hostname} bold />
      {endpoint.ipAddresses && endpoint.ipAddresses.length > 0 && (
        <Field label="IP Addresses" value={endpoint.ipAddresses.join(', ')} mono />
      )}
      {endpoint.deviceType && (
        <Field label="Device Type" value={endpoint.deviceType.replaceAll('-', ' ')} capitalise />
      )}
    </>
  )
}

function SystemDetails({ endpoint }: { endpoint: EndpointForDetails }): ReactElement {
  return (
    <>
      {endpoint.operatingSystem && (
        <Field label="Operating System" value={endpoint.operatingSystem} />
      )}
      {endpoint.osVersion && <Field label="OS Version" value={endpoint.osVersion} />}
    </>
  )
}

function OrganisationalDetails({ endpoint }: { endpoint: EndpointForDetails }): ReactElement {
  return (
    <>
      {endpoint.domain && <Field label="Domain" value={endpoint.domain} />}
      {endpoint.ouPath && <Field label="OU Path" value={endpoint.ouPath} mono breakAll />}
    </>
  )
}

function Header({ onClose }: { onClose: () => void }): ReactElement {
  return (
    <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-5 py-4 dark:border-slate-700 dark:bg-slate-900/50">
      <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Endpoint Details</h3>
      <button
        onClick={onClose}
        className="rounded-md p-1 text-slate-400 hover:bg-slate-200 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-300"
      >
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>
  )
}

interface FieldProps {
  label: string
  value: string
  bold?: boolean
  mono?: boolean
  capitalise?: boolean
  breakAll?: boolean
  idStyle?: boolean
}

function Field({
  label,
  value,
  bold,
  mono,
  capitalise,
  breakAll,
  idStyle,
}: FieldProps): ReactElement {
  const valueClasses = [
    idStyle
      ? 'text-xs font-mono text-slate-400 dark:text-slate-500'
      : 'text-sm text-slate-700 dark:text-slate-300',
    bold && !idStyle ? 'font-semibold text-slate-900 dark:text-slate-100' : '',
    mono ? 'font-mono' : '',
    capitalise ? 'capitalize' : '',
    breakAll ? 'text-xs break-all' : '',
  ]
    .filter(Boolean)
    .join(' ')
  return (
    <div>
      <label className="text-xs font-medium tracking-wider text-slate-500 uppercase dark:text-slate-400">
        {label}
      </label>
      <p className={valueClasses}>{value}</p>
    </div>
  )
}

function RolesField({ roles }: { roles: string[] }): ReactElement {
  return (
    <div>
      <label className="text-xs font-medium tracking-wider text-slate-500 uppercase dark:text-slate-400">
        Roles
      </label>
      <div className="mt-1 flex flex-wrap gap-1">
        {roles.map((role) => (
          <span
            key={role}
            className="inline-flex rounded-full bg-fuchsia-100 px-2 py-0.5 text-xs font-medium text-fuchsia-700 dark:bg-fuchsia-900/30 dark:text-fuchsia-300"
          >
            {role}
          </span>
        ))}
      </div>
    </div>
  )
}

function ImportanceField({ importance }: { importance: string }): ReactElement {
  const colour = importanceColour(importance)
  return (
    <div>
      <label className="text-xs font-medium tracking-wider text-slate-500 uppercase dark:text-slate-400">
        Importance
      </label>
      <p className={`text-sm font-medium capitalize ${colour}`}>{importance}</p>
    </div>
  )
}

function importanceColour(importance: string): string {
  if (importance === 'critical') return 'text-red-600 dark:text-red-400'
  if (importance === 'high') return 'text-orange-600 dark:text-orange-400'
  if (importance === 'medium') return 'text-amber-600 dark:text-amber-400'
  return 'text-emerald-600 dark:text-emerald-400'
}

function NotesField({ notes }: { notes: string }): ReactElement {
  return (
    <div>
      <label className="text-xs font-medium tracking-wider text-slate-500 uppercase dark:text-slate-400">
        Notes
      </label>
      <p className="text-sm whitespace-pre-wrap text-slate-700 dark:text-slate-300">{notes}</p>
    </div>
  )
}
